import { AbLine, FixType, Point, PolygonRing } from "protos/geo/geo";
import { CenterPivotLeg, Farm, FieldData, Zone } from "protos/farm/farm";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { makeGeoId } from "common/utils/geo";

interface ZoneSelection {
  type: "zone";
  selection: Zone;
}
interface PointSelection {
  type: "point";
  selection: Point;
}
interface AbLineSelection {
  type: "abLine";
  selection: AbLine;
}
interface FieldDataSelection {
  type: "fieldData";
  selection: FieldData;
}
export type Selection =
  | ZoneSelection
  | PointSelection
  | AbLineSelection
  | FieldDataSelection;

export interface DraftBoundaryPoint {
  pointId: string;
  lat: number;
  lng: number;
  /**
   * Stable label for this point, assigned when the edit starts and kept until
   * it is saved. Deliberately not the array index: raw GPS ids are impossible
   * to hold in your head, so a point has to keep the same number while you
   * reorder the ring.
   */
  ordinal: number;
  fixType?: FixType;
  isNew?: boolean;
}

// identifies a single polygon ring within a zone. holeIndex is undefined for
// the outer boundary, which is nearly always the case: holes are rare
export interface BoundaryRingId {
  zoneId: string;
  areaIndex: number;
  holeIndex?: number;
}

export type NewZoneKind = "farmBoundary" | "obstacle" | "centerPivot";

/** A zone being added: its ring starts empty and the zone is created on save. */
export interface PendingZone {
  kind: NewZoneKind;
  name: string;
  passable: boolean;
  armLengthMeters?: number;
  /** where the arm is drawn while placing; a pivot does not store it */
  armBearingDegrees?: number;
}

export interface BoundaryEdit extends BoundaryRingId {
  // draft ring, un-closed (no repeated first point at the end)
  points: DraftBoundaryPoint[];
  // the ring as it was when editing started, to detect unsaved changes
  originalPoints: DraftBoundaryPoint[];
  /** the point whose details are open */
  activePointId?: string;
  /** where the active point was when it was opened, so Cancel can put it back */
  activePointOrigin?: Pick<DraftBoundaryPoint, "lat" | "lng" | "fixType">;
  /**
   * The active point is being carried: it follows the cursor over the map and
   * the next map click puts it down there. Picking a point up (clicking it, or
   * its row) starts carrying; placing or dragging it stops.
   */
  carrying: boolean;
  pending?: PendingZone;
}

/**
 * A single point being repositioned on the map from its details panel,
 * outside of any boundary edit. Held here rather than in the panel because
 * the map owns the clicks that move it.
 */
export interface PointMove {
  pointId: string;
  lat: number;
  lng: number;
  /** follows the cursor until the next map click puts it down */
  carrying: boolean;
}

/** a pivot's legs as edited in its details panel, drawn before they are saved */
export interface PivotLegsDraft {
  zoneId: string;
  legs: CenterPivotLeg[];
}

interface State {
  farm?: Farm;
  pointsById?: Record<string, Point>;
  zonesById?: Record<string, Zone>;
  showDetails: boolean;
  shownPointIds: string[];
  selection?: Selection;
  focus?: Selection;
  boundaryEdit?: BoundaryEdit;
  pointMove?: PointMove;
  pivotLegsDraft?: PivotLegsDraft;
  /**
   * The safety warning was closed. Kept here, not in persisted preferences,
   * so it comes back on the next visit: it is a safety notice, not a tip.
   */
  safetyWarningDismissed: boolean;
}

const initialState: State = {
  farm: undefined,
  pointsById: undefined,
  showDetails: false,
  shownPointIds: [],
  selection: undefined,
  safetyWarningDismissed: false,
};

export const farmExplorer = createSlice({
  name: "farmExplorer",
  initialState,
  reducers: {
    setFarm(state, action: PayloadAction<Farm | undefined>) {
      state.farm = action.payload;
      state.selection = findSelection(action.payload, state.selection);
      state.focus = findSelection(action.payload, state.focus);
      // drop an in-progress boundary edit if its ring no longer exists
      if (
        state.boundaryEdit &&
        !state.boundaryEdit.pending &&
        !findRing(action.payload, state.boundaryEdit)
      ) {
        state.boundaryEdit = undefined;
      }
    },
    setPointsById(state, action: PayloadAction<Record<string, Point>>) {
      state.pointsById = action.payload;
    },
    setZonesById(state, action: PayloadAction<Record<string, Zone>>) {
      state.zonesById = action.payload;
    },
    setShowDetails(state, action: PayloadAction<boolean>) {
      state.showDetails = action.payload;
    },
    setShownPointIds(state, action: PayloadAction<string[]>) {
      state.shownPointIds = action.payload;
    },
    addShownPointIds(state, action: PayloadAction<string[]>) {
      state.shownPointIds = [
        ...new Set([...state.shownPointIds, ...action.payload]),
      ];
    },
    removeShownPointIds(state, action: PayloadAction<string[]>) {
      const ids = new Set(state.shownPointIds);
      for (const id of action.payload) {
        ids.delete(id);
      }
      state.shownPointIds = [...ids];
    },
    setSelection(state, action: PayloadAction<Selection | undefined>) {
      state.selection = action.payload;
      // toggles when something is (un)selected
      state.showDetails = action.payload !== undefined;
      // changing the selection abandons any in-progress edit
      state.boundaryEdit = undefined;
      state.pointMove = undefined;

      // hides a point when it is deselected
      if (!action.payload && state.selection?.type === "point") {
        const id = state.selection.selection.id?.id;
        if (id) {
          state.shownPointIds = state.shownPointIds.filter((pid) => pid !== id);
        }
      }
    },
    setPivotLegsDraft(
      state,
      action: PayloadAction<PivotLegsDraft | undefined>
    ) {
      state.pivotLegsDraft = action.payload;
    },
    setFocus(state, action: PayloadAction<Selection | undefined>) {
      state.focus = action.payload;
    },
    startBoundaryEdit(
      state,
      action: PayloadAction<Omit<BoundaryEdit, "originalPoints" | "carrying">>
    ) {
      state.boundaryEdit = {
        ...action.payload,
        originalPoints: action.payload.points,
        carrying: false,
      };
    },
    endBoundaryEdit(state) {
      state.boundaryEdit = undefined;
    },
    startNewZone: {
      reducer(
        state,
        action: PayloadAction<{ kind: NewZoneKind; zoneId: string }>
      ) {
        state.selection = undefined;
        state.focus = undefined;
        state.pointMove = undefined;
        state.showDetails = true;
        state.boundaryEdit = {
          zoneId: action.payload.zoneId,
          areaIndex: 0,
          points: [],
          originalPoints: [],
          carrying: false,
          pending: { kind: action.payload.kind, name: "", passable: false },
        };
      },
      prepare: (kind: NewZoneKind) => ({
        payload: { kind, zoneId: makeGeoId() },
      }),
    },
    updatePendingZone(
      state,
      action: PayloadAction<Partial<Omit<PendingZone, "kind">>>
    ) {
      if (!state.boundaryEdit?.pending) {
        return;
      }
      state.boundaryEdit.pending = {
        ...state.boundaryEdit.pending,
        ...action.payload,
      };
    },
    setBoundaryEditPoints(state, action: PayloadAction<DraftBoundaryPoint[]>) {
      if (!state.boundaryEdit) {
        return;
      }
      state.boundaryEdit.points = action.payload;
      // drop references to points that no longer exist
      const ids = new Set(action.payload.map((p) => p.pointId));
      const { activePointId } = state.boundaryEdit;
      if (activePointId && !ids.has(activePointId)) {
        state.boundaryEdit.activePointId = undefined;
        state.boundaryEdit.activePointOrigin = undefined;
        state.boundaryEdit.carrying = false;
      }
      // a deleted point stays in the farm's pointDefs, and the map draws
      // whatever is focused, so stale focus leaves it on screen as a ghost
      if (state.focus?.type === "point") {
        const focusedId = state.focus.selection.id?.id;
        if (focusedId && !ids.has(focusedId)) {
          state.focus = undefined;
        }
      }
    },
    setBoundaryEditActivePoint(
      state,
      action: PayloadAction<string | undefined>
    ) {
      if (!state.boundaryEdit) {
        return;
      }
      state.boundaryEdit.activePointId = action.payload;
      const point = state.boundaryEdit.points.find(
        (pt) => pt.pointId === action.payload
      );
      state.boundaryEdit.activePointOrigin = point && {
        lat: point.lat,
        lng: point.lng,
        fixType: point.fixType,
      };
      // opening a point picks it up
      state.boundaryEdit.carrying = action.payload !== undefined;
    },
    // closes the active point, putting it back where it was when opened
    cancelBoundaryEditActivePoint(state) {
      if (!state.boundaryEdit) {
        return;
      }
      const { activePointId, activePointOrigin } = state.boundaryEdit;
      if (activePointId && activePointOrigin) {
        state.boundaryEdit.points = state.boundaryEdit.points.map((pt) =>
          pt.pointId === activePointId ? { ...pt, ...activePointOrigin } : pt
        );
      }
      state.boundaryEdit.activePointId = undefined;
      state.boundaryEdit.activePointOrigin = undefined;
      state.boundaryEdit.carrying = false;
    },
    setBoundaryEditCarrying(state, action: PayloadAction<boolean>) {
      if (!state.boundaryEdit?.activePointId) {
        return;
      }
      state.boundaryEdit.carrying = action.payload;
    },
    startPointMove(state, action: PayloadAction<Omit<PointMove, "carrying">>) {
      state.pointMove = { ...action.payload, carrying: true };
    },
    // puts the point down
    setPointMovePosition(
      state,
      action: PayloadAction<{ lat: number; lng: number }>
    ) {
      if (!state.pointMove) {
        return;
      }
      state.pointMove.lat = action.payload.lat;
      state.pointMove.lng = action.payload.lng;
      state.pointMove.carrying = false;
    },
    setPointMoveCarrying(state, action: PayloadAction<boolean>) {
      if (!state.pointMove) {
        return;
      }
      state.pointMove.carrying = action.payload;
    },
    endPointMove(state) {
      state.pointMove = undefined;
    },
    dismissSafetyWarning(state) {
      state.safetyWarningDismissed = true;
    },
  },
  selectors: {
    selectFarmId: (state) => state.farm?.id?.id,
  },
});

export const {
  setFarm,
  setPointsById,
  setZonesById,
  setShowDetails,
  setShownPointIds,
  addShownPointIds,
  removeShownPointIds,
  setSelection,
  setFocus,
  setPivotLegsDraft,
  startBoundaryEdit,
  endBoundaryEdit,
  startNewZone,
  updatePendingZone,
  setBoundaryEditPoints,
  setBoundaryEditActivePoint,
  cancelBoundaryEditActivePoint,
  setBoundaryEditCarrying,
  startPointMove,
  setPointMovePosition,
  setPointMoveCarrying,
  endPointMove,
  dismissSafetyWarning,
} = farmExplorer.actions;

// finds the polygon ring a boundary edit refers to, if it still exists
export const findRing = (
  farm: Farm | undefined,
  ring: BoundaryRingId
): PolygonRing | undefined => {
  const zone = farm?.zones.find((z) => z.id?.id === ring.zoneId);
  const polygon = zone?.areas[ring.areaIndex]?.polygon;
  if (!polygon) {
    return;
  }
  return ring.holeIndex === undefined
    ? polygon.boundary
    : polygon.holes[ring.holeIndex];
};

export const { selectFarmId } = farmExplorer.selectors;

// useful for replacing the selection when you modify the underlying farm.
const findSelection = (
  farm?: Farm,
  selection?: Selection
): Selection | undefined => {
  if (!farm || !selection) {
    return;
  }
  switch (selection.type) {
    case "zone": {
      const zone = farm.zones.find(
        (z) => z.id?.id === selection.selection.id?.id
      );
      return zone
        ? {
            ...selection,
            selection: zone,
          }
        : undefined;
    }
    case "point": {
      const pointDef = farm.pointDefs.find(
        (p) => p.point?.id?.id === selection.selection.id?.id
      );
      return pointDef?.point
        ? {
            ...selection,
            selection: pointDef.point,
          }
        : undefined;
    }
  }
};
